import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  useTheme,
  alpha,
  Chip,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Fade,
  Button
} from '@mui/material';
import {
  Factory as FactoryIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalDrink as LocalDrinkIcon,
  Science as ScienceIcon,
  Speed as SpeedIcon,
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';

const ProductionLineOverviewPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canViewLineProductionOverview } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState({
    totalLineProductions: 0,
    completedLineProductions: 0,
    inProgressLineProductions: 0,
    draftLineProductions: 0,
    totalProductionValue: 0,
    totalProductionVolume: 0,
    averageLineSize: 0,
    topProducts: [],
    recentLineProductions: [],
    costAnalysis: {
    totalCosts: 0,
      averageCostPerLine: 0,
      materialCosts: 0,
      laborCosts: 0,
      overheadCosts: 0
    },
    qualityMetrics: {
      averageYieldPercentage: 0,
      wastePercentage: 0,
      linesWithQualityIssues: 0,
      linesWithVarianceIssues: 0,
      totalVarianceIssues: 0,
      criticalQualityIssues: 0,
      averageCompletionTime: 0,
      workflowCompletionRate: 0,
      safetyComplianceRate: 0,
      supplyUsageEfficiency: 0,
      itemsWithLotTracking: 0,
      totalItems: 0
    },
    resourceUtilization: {
      activeLocations: 0,
      uniqueTemplates: 0,
      totalOperators: 0,
      averageOperatorsPerLine: 0,
      primaryOperatorCoverage: 0,
      linesWithEnvironmentalData: 0,
      avgTemperature: 0,
      avgHumidity: 0
    },
    workflowAnalytics: {
      completedWorkflows: 0,
      totalWorkflows: 0,
      stuckWorkflows: 0,
      averageWorkflowProgress: 0,
      avgStepCompletionTimes: {}
    }
  });

  useEffect(() => {
    if (companyId) {
      loadOverviewData();
    }
  }, [companyId]);
  
  const loadOverviewData = async () => {
    try {
    setLoading(true);
      
      // Load line production data with comprehensive related information
      const { data: lineProductions, error: lineProductionsError } = await supabase
        .from('line_productions')
        .select(`
          *,
          product:product_id(id, name),
          production_line_costs(*),
          production_line_logs(
            id,
            overview_completed,
            area_checklist_completed,
            supply_log_completed,
            summary_log_completed,
            signature_log_completed,
            total_duration_minutes,
            workflow_progress_percentage,
            current_step,
            final_signoff,
            cleaning_status_confirmed,
            temperature_value,
            humidity_percent,
            production_line_operators(operator_name, operator_role, is_primary)
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (lineProductionsError) throw lineProductionsError;

      // Load yield data with line production references
      const { data: yieldData, error: yieldError } = await supabase
        .from('production_line_log_yield')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (yieldError) console.warn('Error loading yield data:', yieldError);

      // Load production line items for quality tracking
      const { data: lineItems, error: itemsError } = await supabase
        .from('production_line_items')
        .select(`
          line_production_id, 
          actual_quantity, 
          planned_quantity,
          item_condition,
          item_type,
          variance_percentage,
          supplier_lot_number,
          supplier_batch_number
        `)
        .eq('company_id', companyId);

      if (itemsError) console.warn('Error loading line items:', itemsError);

      // Load signature data for quality tracking
      const { data: signatures, error: signaturesError } = await supabase
        .from('production_line_log_signatures')
        .select('line_production_id, role, signed_at, signature_type')
        .eq('company_id', companyId);

      if (signaturesError) console.warn('Error loading signatures:', signaturesError);

      // Load checklist data for safety compliance tracking
      const { data: checklists, error: checklistsError } = await supabase
        .from('production_line_log_checklists')
        .select(`
          line_log_id,
          checklist_status,
          all_items_checked,
          line_sanitized,
          equipment_cleaned,
          safety_check_complete,
          area_cleared,
          supplies_verified,
          equipment_calibrated,
          environmental_conditions_verified,
          ppe_verified,
          quality_standards_reviewed,
          work_area_cleared
        `)
        .eq('company_id', companyId);

      if (checklistsError) console.warn('Error loading checklists:', checklistsError);

      // Load supply logs for material usage tracking
      const { data: supplyLogs, error: supplyLogsError } = await supabase
        .from('production_line_log_supplies')
        .select('line_log_id, supply_name, planned_quantity, actual_quantity, item_type')
        .eq('company_id', companyId);

      if (supplyLogsError) console.warn('Error loading supply logs:', supplyLogsError);

      // Process the data with comprehensive analytics
      const processedData = processLineProductionData(
        lineProductions || [], 
        yieldData || [], 
        lineItems || [], 
        signatures || [],
        checklists || [],
        supplyLogs || []
      );
      setOverviewData(processedData);

    } catch (error) {
      console.error('Error loading production line overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const processLineProductionData = (lineProductions, yieldData, lineItems, signatures, checklists, supplyLogs) => {
    const totalLineProductions = lineProductions.length;
    const completedLineProductions = lineProductions.filter(lp => lp.status === 'completed').length;
    const inProgressLineProductions = lineProductions.filter(lp => ['in_production', 'quality_testing'].includes(lp.status)).length;
    const draftLineProductions = lineProductions.filter(lp => lp.status === 'draft').length;

    // Calculate production volume and costs
    let totalProductionValue = 0;
    let totalProductionVolume = 0;
    let totalCosts = 0;
    let materialCosts = 0;
    let laborCosts = 0;
    let overheadCosts = 0;

    lineProductions.forEach(line => {
      if (line.planned_quantity) {
        totalProductionVolume += line.planned_quantity;
      }
      
      // Handle production_line_costs
      if (line.production_line_costs) {
        const costs = Array.isArray(line.production_line_costs) ? line.production_line_costs[0] : line.production_line_costs;
        if (costs) {
          const lineTotalCost = parseFloat(costs.total_cost) || 0;
          const lineMaterialCost = parseFloat(costs.total_material_cost) || 0;
          const lineLaborCost = parseFloat(costs.labor_cost) || 0;
          const lineOverheadCost = parseFloat(costs.overhead_cost) || 0;
          
          totalCosts += lineTotalCost;
          materialCosts += lineMaterialCost;
          laborCosts += lineLaborCost;
          overheadCosts += lineOverheadCost;
        }
      }
    });

    const averageLineSize = totalLineProductions > 0 ? totalProductionVolume / totalLineProductions : 0;
    const averageCostPerLine = totalLineProductions > 0 ? totalCosts / totalLineProductions : 0;
    
    // Top products by line production count
    const productCounts = {};
    lineProductions.forEach(line => {
      if (line.product?.name) {
        productCounts[line.product.name] = (productCounts[line.product.name] || 0) + 1;
      }
    });

    const topProducts = Object.entries(productCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    // Recent line productions (last 5)
    const recentLineProductions = lineProductions.slice(0, 5).map(line => ({
      id: line.id,
      batch_number: line.batch_number,
      product_name: line.product?.name || 'Unknown Product',
      status: line.status,
      planned_quantity: line.planned_quantity,
      planned_unit_of_measure: line.planned_unit_of_measure,
      created_at: line.created_at,
      priority: line.priority
    }));

    // Enhanced Quality Metrics using actual yield data
    let averageYieldPercentage = 0;
    let totalYieldRecords = 0;
    let wastePercentage = 0;
    let totalWasteUnits = 0;
    let totalProducedUnits = 0;
    
    yieldData.forEach(yieldRecord => {
      if (yieldRecord.total_units_produced && yieldRecord.line_production_id) {
        const lineProduction = lineProductions.find(lp => lp.id === yieldRecord.line_production_id);
        if (lineProduction?.planned_quantity) {
          const yieldPercentage = (yieldRecord.total_units_produced / lineProduction.planned_quantity) * 100;
          averageYieldPercentage += yieldPercentage;
          totalYieldRecords++;
          
          // Track waste
          totalProducedUnits += yieldRecord.total_units_produced;
          totalWasteUnits += yieldRecord.units_set_aside || 0;
        }
      }
    });
    
    if (totalYieldRecords > 0) {
      averageYieldPercentage = averageYieldPercentage / totalYieldRecords;
    }
    
    if (totalProducedUnits > 0) {
      wastePercentage = (totalWasteUnits / (totalProducedUnits + totalWasteUnits)) * 100;
    }

    // Calculate completion times from actual log data
    let totalCompletionTime = 0;
    let completionTimeCount = 0;
    
    lineProductions.forEach(line => {
      if (line.production_line_logs?.[0]?.total_duration_minutes) {
        totalCompletionTime += line.production_line_logs[0].total_duration_minutes;
        completionTimeCount++;
      }
    });
    
    const averageCompletionTime = completionTimeCount > 0 ? totalCompletionTime / completionTimeCount : 0;

    // Enhanced Workflow Analytics using actual workflow progress data
    let totalWorkflows = 0;
    let completedWorkflows = 0;
    let totalWorkflowProgress = 0;
    let stuckWorkflows = 0;
    let avgStepCompletionTimes = {};
    
    lineProductions.forEach(line => {
      if (line.production_line_logs?.[0]) {
        const log = line.production_line_logs[0];
        totalWorkflows++;
        
        // Use actual workflow_progress_percentage if available
        if (log.workflow_progress_percentage !== null && log.workflow_progress_percentage !== undefined) {
          totalWorkflowProgress += log.workflow_progress_percentage;
        } else {
          // Fallback to manual calculation
          const steps = [
            log.overview_completed,
            log.area_checklist_completed,
            log.supply_log_completed,
            log.summary_log_completed,
            log.signature_log_completed
          ];
          
          const completedSteps = steps.filter(step => step === true).length;
          const progressPercentage = (completedSteps / steps.length) * 100;
          totalWorkflowProgress += progressPercentage;
        }
        
        // Check if workflow is complete (final signoff)
        if (log.final_signoff === true) {
          completedWorkflows++;
        }
        
        // Check if workflow is stuck (no progress in current step for extended time)
        if (log.current_step && log.current_step !== 'completed' && log.workflow_progress_percentage < 100) {
          stuckWorkflows++;
        }
      }
    });
    
    const averageWorkflowProgress = totalWorkflows > 0 ? totalWorkflowProgress / totalWorkflows : 0;
    const workflowCompletionRate = totalWorkflows > 0 ? (completedWorkflows / totalWorkflows) * 100 : 0;

    // Enhanced Quality Issues Analysis
    const lineItemsByProduction = {};
    let totalVarianceIssues = 0;
    let criticalQualityIssues = 0;
    let itemsWithLotTracking = 0;
    
    lineItems.forEach(item => {
      if (!lineItemsByProduction[item.line_production_id]) {
        lineItemsByProduction[item.line_production_id] = [];
      }
      lineItemsByProduction[item.line_production_id].push(item);
      
      // Track variance issues (>10% variance)
      if (item.variance_percentage && Math.abs(item.variance_percentage) > 10) {
        totalVarianceIssues++;
      }
      
      // Track critical quality issues (Damaged, Expired)
      if (item.item_condition && ['Damaged', 'Expired', 'damaged', 'expired'].includes(item.item_condition)) {
        criticalQualityIssues++;
      }
      
      // Track lot/batch traceability
      if (item.supplier_lot_number || item.supplier_batch_number) {
        itemsWithLotTracking++;
      }
    });

    let linesWithQualityIssues = 0;
    let linesWithVarianceIssues = 0;
    let linesWithoutItemTracking = 0;
    
    Object.keys(lineItemsByProduction).forEach(lineProductionId => {
      const items = lineItemsByProduction[lineProductionId];
      
      // Quality issues (damaged/expired items)
      const hasQualityIssues = items.some(item => 
        item.item_condition && ['Damaged', 'Expired', 'damaged', 'expired'].includes(item.item_condition)
      );
      if (hasQualityIssues) {
        linesWithQualityIssues++;
      }
      
      // Variance issues (>10% variance from planned)
      const hasVarianceIssues = items.some(item => 
        item.variance_percentage && Math.abs(item.variance_percentage) > 10
      );
      if (hasVarianceIssues) {
        linesWithVarianceIssues++;
      }
      
      // Missing lot/batch tracking
      const missingTracking = items.some(item => 
        !item.supplier_lot_number && !item.supplier_batch_number
      );
      if (missingTracking) {
        linesWithoutItemTracking++;
      }
    });

    // Safety Compliance Analysis from checklists
    let totalSafetyChecks = 0;
    let passedSafetyChecks = 0;
    let criticalSafetyFailures = 0;
    
    checklists.forEach(checklist => {
      totalSafetyChecks++;
      if (checklist.all_items_checked === true) {
        passedSafetyChecks++;
      }
      
      // Critical safety items
      const criticalItems = [
        checklist.safety_check_complete,
        checklist.equipment_cleaned,
        checklist.ppe_verified,
        checklist.environmental_conditions_verified
      ];
      
      if (criticalItems.some(item => item === false)) {
        criticalSafetyFailures++;
      }
    });
    
    const safetyComplianceRate = totalSafetyChecks > 0 ? (passedSafetyChecks / totalSafetyChecks) * 100 : 0;

    // Material Usage Efficiency from supply logs
    let totalMaterialVariance = 0;
    let materialVarianceCount = 0;
    let supplyUsageEfficiency = 0;
    
    supplyLogs.forEach(supply => {
      if (supply.planned_quantity && supply.actual_quantity) {
        const variance = Math.abs(supply.actual_quantity - supply.planned_quantity) / supply.planned_quantity * 100;
        totalMaterialVariance += variance;
        materialVarianceCount++;
      }
    });
    
    supplyUsageEfficiency = materialVarianceCount > 0 ? 
      Math.max(0, 100 - (totalMaterialVariance / materialVarianceCount)) : 0;

    // Resource utilization with enhanced metrics
    const uniqueLocations = new Set(lineProductions.map(lp => lp.production_location).filter(Boolean)).size;
    const uniqueTemplates = new Set(lineProductions.map(lp => lp.template_id).filter(Boolean)).size;
    
    let totalOperators = 0;
    let linesWithOperators = 0;
    let primaryOperatorLines = 0;
    
    lineProductions.forEach(line => {
      if (line.production_line_logs?.[0]?.production_line_operators) {
        const operators = line.production_line_logs[0].production_line_operators;
        if (Array.isArray(operators) && operators.length > 0) {
          totalOperators += operators.length;
          linesWithOperators++;
          
          // Check if has primary operator
          if (operators.some(op => op.is_primary === true)) {
            primaryOperatorLines++;
          }
        }
      }
    });
    
    const averageOperatorsPerLine = linesWithOperators > 0 ? totalOperators / linesWithOperators : 0;
    const primaryOperatorCoverage = linesWithOperators > 0 ? (primaryOperatorLines / linesWithOperators) * 100 : 0;

    // Environmental Conditions Analysis
    let linesWithEnvironmentalData = 0;
    let avgTemperature = 0;
    let avgHumidity = 0;
    let tempCount = 0;
    let humidityCount = 0;
    
    lineProductions.forEach(line => {
      if (line.production_line_logs?.[0]) {
        const log = line.production_line_logs[0];
        
        if (log.temperature_value || log.humidity_percent) {
          linesWithEnvironmentalData++;
        }
        
        if (log.temperature_value) {
          avgTemperature += log.temperature_value;
          tempCount++;
        }
        
        if (log.humidity_percent) {
          avgHumidity += log.humidity_percent;
          humidityCount++;
        }
      }
    });
    
    avgTemperature = tempCount > 0 ? avgTemperature / tempCount : 0;
    avgHumidity = humidityCount > 0 ? avgHumidity / humidityCount : 0;

    return {
      totalLineProductions,
      completedLineProductions,
      inProgressLineProductions,
      draftLineProductions,
      totalProductionValue,
      totalProductionVolume,
      averageLineSize,
      topProducts,
      recentLineProductions,
      costAnalysis: {
        totalCosts,
        averageCostPerLine,
        materialCosts,
        laborCosts,
        overheadCosts
      },
      qualityMetrics: {
        averageYieldPercentage,
        wastePercentage,
        linesWithQualityIssues,
        linesWithVarianceIssues,
        totalVarianceIssues,
        criticalQualityIssues,
        averageCompletionTime,
        workflowCompletionRate,
        safetyComplianceRate,
        supplyUsageEfficiency,
        itemsWithLotTracking,
        totalItems: lineItems.length
      },
      resourceUtilization: {
        activeLocations: uniqueLocations,
        uniqueTemplates,
        totalOperators,
        averageOperatorsPerLine,
        primaryOperatorCoverage,
        linesWithEnvironmentalData,
        avgTemperature,
        avgHumidity
      },
      workflowAnalytics: {
        completedWorkflows,
        totalWorkflows,
        stuckWorkflows,
        averageWorkflowProgress,
        avgStepCompletionTimes
      }
    };
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatNumber = (num) => (num || 0).toLocaleString();
  const formatDecimal = (num, decimals = 1) => (num || 0).toFixed(decimals);

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      in_production: 'warning',
      quality_testing: 'info',
      draft: 'default',
      scheduled: 'primary',
      approved: 'info',
      on_hold: 'warning',
      cancelled: 'error',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      normal: 'primary',
      high: 'warning',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  // Permission check
  if (!canViewLineProductionOverview()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Line Production Overview Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Line Production Overview. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <AssessmentIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Line Production Overview
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing line production performance and workflow analytics...
        </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in={true} timeout={800} easing="cubic-bezier(0.4, 0, 0.2, 1)">
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 3, pb: 16 }}>
      {/* Enhanced Header */}
      <Box sx={{ 
        display: 'flex', 
        mt: 5,
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 3,
        p: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'info.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 }
              }
            }} />
            <Typography variant="caption" sx={{ 
              color: 'info.main', 
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.75rem'
            }}>
              Performance Insights
            </Typography>
          </Box>
          
          <Typography variant="h4" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '1.8rem',
            mb: 1,
            lineHeight: 1.2
          }}>
        Line Production Overview
      </Typography>
      
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '0.9rem',
            mb: 2.5,
            maxWidth: '400px'
          }}>
            Real-time line production performance and workflow analytics with quality tracking
      </Typography>
      
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip 
              label={`${overviewData.totalLineProductions} ${overviewData.totalLineProductions === 1 ? 'Line' : 'Lines'}`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: 'info.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }} 
            />
            <Chip 
              label={`${overviewData.completedLineProductions} Completed`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }} 
            />
            <Chip 
              label={`${overviewData.inProgressLineProductions} Active`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: 'warning.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
              }} 
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Row 1: Key Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <PrecisionManufacturingIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatNumber(overviewData.totalLineProductions)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Line Productions
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Chip 
                  label={`${overviewData.completedLineProductions} Complete`}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
                <Chip 
                  label={`${overviewData.inProgressLineProductions} Active`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatCurrency(overviewData.costAnalysis.totalCosts)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Production Costs
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Avg per line: {formatCurrency(overviewData.costAnalysis.averageCostPerLine)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.info.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <LocalDrinkIcon sx={{ fontSize: 32, color: theme.palette.info.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatNumber(overviewData.totalProductionVolume)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Units Produced
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Avg line: {formatNumber(overviewData.averageLineSize)} units
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <AssessmentIcon sx={{ fontSize: 32, color: theme.palette.warning.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatDecimal(overviewData.qualityMetrics.averageYieldPercentage)}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Average Yield
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Workflow Rate: {formatDecimal(overviewData.qualityMetrics.workflowCompletionRate)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 2: Recent Line Productions */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <TimelineIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Recent Line Production Activity
                </Typography>
              </Stack>
              
              {overviewData.recentLineProductions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <PrecisionManufacturingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    No recent line productions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start creating line productions to see activity here
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {overviewData.recentLineProductions.map((line) => (
                    <Box key={line.id} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                      bgcolor: 'white',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        transform: 'translateX(4px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3)
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: alpha(getStatusColor(line.status) === 'success' ? theme.palette.success.main : 
                                       getStatusColor(line.status) === 'warning' ? theme.palette.warning.main :
                                       getStatusColor(line.status) === 'error' ? theme.palette.error.main :
                                       theme.palette.primary.main, 0.1),
                          color: getStatusColor(line.status) === 'success' ? theme.palette.success.main : 
                                 getStatusColor(line.status) === 'warning' ? theme.palette.warning.main :
                                 getStatusColor(line.status) === 'error' ? theme.palette.error.main :
                                 theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 3
                        }}>
                          <PrecisionManufacturingIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {line.batch_number}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {line.product_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {line.planned_quantity} {line.planned_unit_of_measure} • {new Date(line.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={line.status.replace(/_/g, ' ').toUpperCase()}
                          color={getStatusColor(line.status)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        {line.priority !== 'normal' && (
                          <Chip 
                            label={line.priority.toUpperCase()}
                            color={getPriorityColor(line.priority)}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
        </Card>
        </Grid>

        {/* Row 3: Top Products by Line Production Count */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Top Products by Line Productions
                </Typography>
              </Stack>
              
              {overviewData.topProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No production data available
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {overviewData.topProducts.map((product, index) => (
                    <Box key={product.name} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transform: 'translateX(4px)'
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          fontWeight: 500
                        }}>
                          {index + 1}
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Product
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                          {product.count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          lines
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 3: Cost Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Average Cost Breakdown
                </Typography>
              </Stack>
              
              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Avg Material Costs
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(overviewData.totalLineProductions > 0 ? overviewData.costAnalysis.materialCosts / overviewData.totalLineProductions : 0)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={overviewData.costAnalysis.totalCosts > 0 ? (overviewData.costAnalysis.materialCosts / overviewData.costAnalysis.totalCosts) * 100 : 0}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.success.main,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Avg Labor Costs
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(overviewData.totalLineProductions > 0 ? overviewData.costAnalysis.laborCosts / overviewData.totalLineProductions : 0)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={overviewData.costAnalysis.totalCosts > 0 ? (overviewData.costAnalysis.laborCosts / overviewData.costAnalysis.totalCosts) * 100 : 0}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.warning.main,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Avg Overhead Costs
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(overviewData.totalLineProductions > 0 ? overviewData.costAnalysis.overheadCosts / overviewData.totalLineProductions : 0)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={overviewData.costAnalysis.totalCosts > 0 ? (overviewData.costAnalysis.overheadCosts / overviewData.costAnalysis.totalCosts) * 100 : 0}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.info.main,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 4: Quality & Safety Metrics */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Quality & Safety Metrics
                </Typography>
              </Stack>
              
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 1 }}>
                      {formatDecimal(overviewData.qualityMetrics.averageYieldPercentage)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      AVG YIELD
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.info.main, mb: 1 }}>
                      {formatDecimal(overviewData.qualityMetrics.safetyComplianceRate)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      SAFETY COMPLIANCE
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.error.main, mb: 1 }}>
                      {overviewData.qualityMetrics.criticalQualityIssues}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      CRITICAL ISSUES
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.warning.main, mb: 1 }}>
                      {formatDecimal(overviewData.qualityMetrics.wastePercentage)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      WASTE RATE
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 4: Material Usage Efficiency */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <SpeedIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Material Usage Efficiency
                </Typography>
              </Stack>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                      {formatDecimal(overviewData.qualityMetrics.supplyUsageEfficiency)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      USAGE EFFICIENCY
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.warning.main, mb: 0.5 }}>
                      {overviewData.qualityMetrics.totalVarianceIssues}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      VARIANCE ISSUES
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                      Lot Traceability Coverage
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={overviewData.qualityMetrics.totalItems > 0 ? (overviewData.qualityMetrics.itemsWithLotTracking / overviewData.qualityMetrics.totalItems) * 100 : 0}
                      sx={{ 
                        height: 12, 
                        borderRadius: 6,
                        bgcolor: alpha(theme.palette.grey[300], 0.3),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: theme.palette.info.main,
                          borderRadius: 6
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {overviewData.qualityMetrics.totalItems > 0 ? Math.round((overviewData.qualityMetrics.itemsWithLotTracking / overviewData.qualityMetrics.totalItems) * 100) : 0}% items have lot/batch tracking
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 5: Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <AssignmentIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Line Production Status
                </Typography>
              </Stack>
              
              <Stack spacing={2}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Completed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                    {overviewData.completedLineProductions}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    In Progress
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                    {overviewData.inProgressLineProductions}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.grey[400], 0.04),
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.1)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Draft
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {overviewData.draftLineProductions}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 5: Workflow Intelligence */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <ScienceIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Workflow Intelligence
                </Typography>
              </Stack>
              
              <Stack spacing={3}>
                <Box sx={{ 
                  p: 3,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.info.main, mb: 1 }}>
                    {formatDecimal(overviewData.workflowAnalytics.averageWorkflowProgress)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                    Avg Workflow Progress
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {overviewData.workflowAnalytics.completedWorkflows}/{overviewData.workflowAnalytics.totalWorkflows} workflows complete
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.warning.main, mb: 0.5 }}>
                        {overviewData.workflowAnalytics.stuckWorkflows}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        STUCK WORKFLOWS
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                        {formatDecimal(overviewData.resourceUtilization.primaryOperatorCoverage)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        PRIMARY OP COVERAGE
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 6: Environmental Conditions & Operations */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <BusinessIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Environmental Conditions
                </Typography>
              </Stack>
              
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.warning.main, mb: 1 }}>
                      {formatDecimal(overviewData.resourceUtilization.avgTemperature)}°F
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      AVG TEMPERATURE
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.info.main, mb: 1 }}>
                      {formatDecimal(overviewData.resourceUtilization.avgHumidity)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      AVG HUMIDITY
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 1 }}>
                      {overviewData.resourceUtilization.linesWithEnvironmentalData}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      LINES W/ ENV DATA
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 1 }}>
                      {formatDecimal(overviewData.resourceUtilization.averageOperatorsPerLine)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      AVG OPERATORS
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 6: Resource Utilization */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <InventoryIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Resource Utilization
                </Typography>
              </Stack>
              
              <Stack spacing={3}>
                <Box sx={{ 
                  p: 3,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 1 }}>
                    {overviewData.resourceUtilization.totalOperators}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                    Total Operators Active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Across {overviewData.resourceUtilization.activeLocations} production locations
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                        {overviewData.resourceUtilization.uniqueTemplates}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        TEMPLATES
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.info.main, mb: 0.5 }}>
                        {overviewData.resourceUtilization.activeLocations}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        LOCATIONS
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Container>
    </Fade>
  );
};

export default ProductionLineOverviewPage;
